import apiService from './api';

export interface Organization {
  id: number;
  name: string;
  description?: string;
  timezone?: string;
  pay_cycle_start_day?: number;
  fortnightly_cycle_reference_date?: string | null;
  clock_grace_minutes?: number;
  contact_email?: string | null;
  contact_phone?: string | null;
  website?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  abn?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateOrganizationRequest {
  name: string;
  description?: string;
  timezone?: string;
  contact_email?: string;
  contact_phone?: string;
  website?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
  abn?: string;
}

export interface UpdateOrganizationRequest {
  name?: string;
  description?: string;
  timezone?: string;
  contact_email?: string | null;
  contact_phone?: string | null;
  website?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  abn?: string | null;
}

export interface OrgSearchResult {
  id: number;
  name: string;
  description?: string;
  stakeholder_types: string[];
}

class OrganizationService {
  // Create a new organization
  async create(data: CreateOrganizationRequest) {
    const response = await apiService.post<Organization>('/organisations', data);
    return response;
  }

  // Get current user's organization
  async getCurrentOrganization() {
    const response = await apiService.get<Organization>('/organisations/current');
    return response;
  }

  // Update organization
  async update(id: number, data: UpdateOrganizationRequest) {
    const response = await apiService.put<Organization>(`/organisations/${id}`, data);
    return response;
  }

  // Get organization members
  async getMembers(organizationId: number) {
    const response = await apiService.get(`/organisations/${organizationId}/members`);
    return response;
  }

  // Super admin: list all orgs as {id, name}
  async listAll() {
    const response = await apiService.get<{ id: number; name: string }[]>('/organisations/list-all');
    return response;
  }

  // Get current org's stakeholder types (no module subscription required)
  async getOrgTypes() {
    return apiService.get<{ stakeholder_type: string }[]>('/organisations/types');
  }

  // Add a stakeholder type to the current org
  async addOrgType(stakeholder_type: string) {
    return apiService.post<{ stakeholder_type: string }>('/organisations/types', { stakeholder_type });
  }

  // Remove a stakeholder type from the current org
  async removeOrgType(type: string) {
    return apiService.delete(`/organisations/types/${type}`);
  }

  // Search organisations by name and/or stakeholder type
  async searchOrganizations(params: { name?: string; stakeholder_type?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.name) qs.append('name', params.name);
    if (params.stakeholder_type) qs.append('stakeholder_type', params.stakeholder_type);
    const response = await apiService.get<OrgSearchResult[]>(`/organisations/search${qs.toString() ? `?${qs}` : ''}`);
    return response;
  }
}

export default new OrganizationService();
